import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Box,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import { Tooltip } from "../../../../components";
import { LabelValue } from "../../../../components/LabelValue";
import { useLocalization } from "../../../../hooks/useLocalization";
import { useClampOverflow } from "../../hooks/useClampOverflow";
import { TExperienceInfoProps } from "../../types";
import { blocksTooltip, ymd } from "../../utils";

const DESKTOP_LABEL_RAIL = 152;
const VALUE_GAP = 16;
const THUMBNAIL_W = 232;
const THUMBNAIL_H = 138;

const ThumbnailBox: React.FC<{
  imageUrl?: string | null;
  experienceName: string;
  fontSize: number; // 14 mobile / 16 desktop
}> = ({ imageUrl, experienceName, fontSize }) => {
  const theme = useTheme();
  const [imgError, setImgError] = useState(false);

  // ---- image fallback handling ----
  const src = (imageUrl ?? "").trim() || undefined;

  React.useEffect(() => setImgError(false), [src]);

  const showImage = !!src && !imgError;

  return (
    <Box
      sx={{
        width: THUMBNAIL_W,
        height: THUMBNAIL_H,
        borderRadius: "16px",
        bgcolor: theme.colors.secondary[0],
        filter: "drop-shadow(0px 0px 20px rgba(0,0,0,0.10))",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        px: showImage ? 0 : { xs: 1, md: 1.5 },
      }}
    >
      {showImage ? (
        <img
          alt={experienceName}
          fetchPriority="high"
          loading="eager"
          onError={() => setImgError(true)}
          src={src}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        <Typography
          sx={{
            fontWeight: 600,
            fontSize,
            color: theme.colors.text[2],
            textAlign: "center",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
          title={experienceName}
        >
          {experienceName}
        </Typography>
      )}
    </Box>
  );
};

const DescriptionField: React.FC<{
  description?: string | null;
  lines: number; // 4 mobile, 3 desktop
}> = ({ description, lines }) => {
  const { t } = useLocalization();
  const theme = useTheme();
  const [expanded, setExpanded] = React.useState(false);
  const { ref, clampSx, showToggle, hasText } = useClampOverflow(
    description,
    expanded,
    lines,
  );

  return (
    <>
      <Typography
        component="span"
        ref={ref}
        sx={{
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          whiteSpace: "normal",
          lineHeight: "20px",
          color: theme.colors.text[5],
          fontWeight: 500,
          fontSize: 14,
          ...clampSx,
        }}
        title={description ?? undefined}
      >
        {hasText ? description : "—"}
      </Typography>

      {showToggle && (
        <Typography
          component="button"
          onClick={() => setExpanded((v) => !v)}
          sx={{
            mt: 0.5,
            border: 0,
            p: 0,
            background: "transparent",
            cursor: "pointer",
            color: theme.palette.primary.main,
            fontWeight: 600,
            fontSize: 12,
          }}
          type="button"
        >
          {expanded
            ? t("experiences.overviewTab.showLess")
            : t("experiences.overviewTab.showMore")}
        </Typography>
      )}
    </>
  );
};

const BlockedSeatsValue: React.FC<{
  blocked: number;
  isExternalLimited: boolean;
  showRenewalOnBlocked: boolean;
  renewalDateISO: string;
}> = ({ blocked, isExternalLimited, showRenewalOnBlocked, renewalDateISO }) => {
  const { t } = useLocalization();
  const theme = useTheme();

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Typography
        sx={{ color: theme.colors.text[5], fontWeight: 500, fontSize: 14 }}
      >
        {blocked}
      </Typography>
      {isExternalLimited && showRenewalOnBlocked && (
        <Typography
          component="span"
          sx={{ color: theme.colors.text[5], fontWeight: 500, fontSize: 14 }}
        >
          {t("experiences.overviewTab.until")} {renewalDateISO}
        </Typography>
      )}
      {isExternalLimited && (
        <Tooltip
          message={blocksTooltip(showRenewalOnBlocked, t)}
          placement="right"
        >
          <IconButton size="small" sx={{ ml: 0.5 }}>
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

/* --------------------------------- views ---------------------------------- */

const MobileView: React.FC<TExperienceInfoProps> = (p) => {
  const { t } = useLocalization();

  return (
    <Stack spacing="24px" sx={{ width: "100%", minWidth: 0 }}>
      <LabelValue label={t("experiences.overviewTab.thumbnail")}>
        <ThumbnailBox
          experienceName={p.experienceName}
          fontSize={14}
          imageUrl={p.imageUrl}
        />
      </LabelValue>

      <LabelValue
        label={t("experiences.overviewTab.company")}
        value={p.companyName}
      />

      <LabelValue
        boxSx={{ maxWidth: 680 }}
        containerSx={{ alignItems: "flex-start", height: "auto" }}
        label={t("experiences.overviewTab.description")}
      >
        <DescriptionField description={p.description} lines={4} />
      </LabelValue>

      {/* <LabelValue label="Version" value={p.version ?? "—"} /> */}

      <LabelValue
        label={t("experiences.overviewTab.expirationDate")}
        value={
          p.expiresAt
            ? ymd(p.expiresAt)
            : t("experiences.overviewTab.unlimited")
        }
      />

      <LabelValue
        label={t("experiences.overviewTab.totalSeats")}
        value={
          p.totalSeats == null
            ? `${p.consumed} / ∞`
            : `${p.consumed} / ${p.totalSeats}`
        }
      />

      {p.totalSeats != null && (
        <LabelValue label={t("experiences.overviewTab.blockedSeats")}>
          <BlockedSeatsValue
            blocked={p.blocked}
            isExternalLimited={p.isExternalLimited}
            renewalDateISO={p.renewalDateISO}
            showRenewalOnBlocked={p.showRenewalOnBlocked}
          />
        </LabelValue>
      )}
    </Stack>
  );
};

const DesktopView: React.FC<TExperienceInfoProps> = (p) => {
  const { t } = useLocalization();

  return (
    <Stack spacing="24px" sx={{ width: "100%", minWidth: 0 }}>
      <LabelValue
        containerSx={{ height: THUMBNAIL_H, alignItems: "center" }}
        label={t("experiences.overviewTab.thumbnail")}
        labelWidth={`${DESKTOP_LABEL_RAIL}px`}
        spacing={VALUE_GAP / 8}
      >
        <ThumbnailBox
          experienceName={p.experienceName}
          fontSize={16}
          imageUrl={p.imageUrl}
        />
      </LabelValue>

      <LabelValue
        label={t("experiences.overviewTab.company")}
        labelWidth={`${DESKTOP_LABEL_RAIL}px`}
        spacing={VALUE_GAP / 8}
        value={p.companyName}
      />

      <LabelValue
        boxSx={{
          maxWidth: 680,
          display: "flex",
          alignItems: "flex-start",
        }}
        containerSx={{
          alignItems: "center",
          height: "auto",
        }}
        label={t("experiences.overviewTab.description")}
        labelWidth={`${DESKTOP_LABEL_RAIL}px`}
        spacing={VALUE_GAP / 8}
      >
        <DescriptionField description={p.description} lines={3} />
      </LabelValue>

      {/* <LabelValue
        label="Version"
        labelWidth={`${DESKTOP_LABEL_RAIL}px`}
        spacing={VALUE_GAP / 8}
        value={p.version ?? "—"}
      /> */}

      <LabelValue
        label={t("experiences.overviewTab.expirationDate")}
        labelWidth={`${DESKTOP_LABEL_RAIL}px`}
        spacing={VALUE_GAP / 8}
        value={
          p.expiresAt
            ? ymd(p.expiresAt)
            : t("experiences.overviewTab.unlimited")
        }
      />

      <LabelValue
        label={t("experiences.overviewTab.totalSeats")}
        labelWidth={`${DESKTOP_LABEL_RAIL}px`}
        spacing={VALUE_GAP / 8}
        value={
          p.totalSeats == null
            ? `${p.consumed} / ∞`
            : `${p.consumed} / ${p.totalSeats}`
        }
      />

      {p.totalSeats != null && (
        <LabelValue
          label={t("experiences.overviewTab.blockedSeats")}
          labelWidth={`${DESKTOP_LABEL_RAIL}px`}
          spacing={VALUE_GAP / 8}
        >
          <BlockedSeatsValue
            blocked={p.blocked}
            isExternalLimited={p.isExternalLimited}
            renewalDateISO={p.renewalDateISO}
            showRenewalOnBlocked={p.showRenewalOnBlocked}
          />
        </LabelValue>
      )}
    </Stack>
  );
};

/* ------------------------------- main export ------------------------------ */

export const ExperienceInfo: React.FC<TExperienceInfoProps> = (props) => {
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up("md"));

  return mdUp ? <DesktopView {...props} /> : <MobileView {...props} />;
};
